import { createTheme as MUICreateTheme } from '@mui/material/styles';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';

import { Tokens, Theme } from '../types';
import {
  generalTokens,
  generalDarkTokens,
  otpTokens,
  vkTokens,
  vkDarkTokens,
} from '../tokens';
import fonts from '../fonts';

import { createPalette } from './createPalette';

declare module '@mui/material/styles' {
  interface Theme {
    tokens: Tokens;
    px: (pixels: number) => number;
  }

  interface ThemeOptions {
    tokens: Tokens;
    px: (pixels: number) => number;
  }
}

/**
 * Создаёт конфигурацию темы на основе предоставленных токенов.
 *
 * @param options - Параметры для создания темы.
 * @returns Объект конфигурации темы, совместимый с Material-UI.
 */
export const createTheme = (options: Theme = {}) => {
  const {
    portal,
    useContainerQuery = false,
    theme = 'general',
    tokens: tokensOverride = {},
  } = options;
  let baseTokens: Tokens = generalTokens;

  if (theme === 'vk') {
    baseTokens = vkTokens;
  } else if (theme === 'otp') {
    baseTokens = otpTokens;
  } else if (theme === 'general-dark') {
    baseTokens = { ...generalDarkTokens, name: 'general' };
  } else if (theme === 'vk-dark') {
    baseTokens = { ...vkDarkTokens, name: 'vk' };
  }

  const mergedTokens: Tokens = merge(cloneDeep(baseTokens), tokensOverride);
  const tokens: Tokens = merge(cloneDeep(mergedTokens), {
    spacing: {
      '0': mergedTokens.spacing['0'] * mergedTokens.density,
      '2': mergedTokens.spacing['2'] * mergedTokens.density,
      '4': mergedTokens.spacing['4'] * mergedTokens.density,
      '8': mergedTokens.spacing['8'] * mergedTokens.density,
      '12': mergedTokens.spacing['12'] * mergedTokens.density,
      '16': mergedTokens.spacing['16'] * mergedTokens.density,
      '20': mergedTokens.spacing['20'] * mergedTokens.density,
      '24': mergedTokens.spacing['24'] * mergedTokens.density,
      '32': mergedTokens.spacing['32'] * mergedTokens.density,
      '40': mergedTokens.spacing['40'] * mergedTokens.density,
      '48': mergedTokens.spacing['48'] * mergedTokens.density,
      '56': mergedTokens.spacing['56'] * mergedTokens.density,
      '72': mergedTokens.spacing['72'] * mergedTokens.density,
      '96': mergedTokens.spacing['96'] * mergedTokens.density,
      '144': mergedTokens.spacing['144'] * mergedTokens.density,
    },
  });

  const px = (pixels: number) => {
    return pixels * tokens.density;
  };

  const typeCssQuery = useContainerQuery ? `@container` : '@media';

  return MUICreateTheme({
    components: {
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
      },
      ...(portal?.current
        ? {
            MuiModal: {
              defaultProps: {
                container: portal.current,
                disableEnforceFocus: true,
              },
            },
            MuiPopover: {
              defaultProps: {
                container: portal.current,
              },
            },
            MuiPopper: {
              defaultProps: {
                container: portal.current,
              },
            },
          }
        : {}),
      MuiCssBaseline: {
        styleOverrides: `
          ${fonts}
          a {
            color: ${tokens.colors.text.light.primary};
          }
          a:hover {
            text-decoration: none;
          }
          canvas {
            border: ${tokens.border.s}px solid ${tokens.colors.stroke.secondary};
          }
          b {
            color: ${tokens.colors.text.light.primary};
          }
        `,
      },
      MuiSvgIcon: {
        styleOverrides: {
          colorDisabled: {
            color: tokens.colors.stroke.secondary,
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.l,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            fontWeight: tokens.typography.variant.h6.fontWeight,
            borderRadius: tokens.radius.l,
            fontSize: tokens.typography.variant.h6.fontSize,
            textTransform: 'none',
            padding: `${tokens.spacing['12']}px ${tokens.spacing['24']}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&:hover, &:active': {
              boxShadow: 'none',
            },
            '&:disabled': {
              background: tokens.colors.bg.greyscale.primary,
              color: tokens.colors.text.light.tertirary,
            },
          },
          sizeLarge: {
            padding: `${tokens.spacing['16']}px ${tokens.spacing['24']}px`,
          },
          sizeSmall: {
            padding: `${tokens.spacing['4']}px ${tokens.spacing['12']}px`,
            fontSize: tokens.typography.variant.body3.fontSize,
            fontWeight: tokens.typography.variant.body3.fontWeight,
            borderRadius: tokens.radius.m,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h3: {
            fontWeight: tokens.typography.variant.h5.fontWeight,
            fontSize: tokens.typography.variant.h5.fontSize,
            lineHeight: tokens.typography.variant.h5.lineHeight,
            letterSpacing: tokens.typography.variant.h5.letterSpacing,
            [`@media (min-width: ${tokens.breakpoints.md}px)`]: {
              fontSize: tokens.typography.variant.h4.fontSize,
              lineHeight: tokens.typography.variant.h4.lineHeight,
            },
          },
          h4: {
            letterSpacing: tokens.typography.variant.h4.letterSpacing,
          },
          h5: {
            fontWeight: tokens.typography.variant.h5.fontWeight,
          },
          h6: {
            fontWeight: tokens.typography.variant.h6.fontWeight,
            fontSize: tokens.typography.variant.h6.fontSize,
            lineHeight: tokens.typography.variant.h6.lineHeight,
          },
          body1: {
            fontSize: tokens.typography.variant.body2.fontSize,
            lineHeight: tokens.typography.variant.body3.lineHeight,
            [`@media (min-width: ${tokens.breakpoints.md}px)`]: {
              fontSize: tokens.typography.variant.body1.fontSize,
              lineHeight: tokens.typography.variant.body1.lineHeight,
            },
          },
          body2: {
            color: tokens.colors.text.light.tertirary,
            fontSize: tokens.typography.variant.body2.fontSize,
            lineHeight: tokens.typography.variant.body3.lineHeight,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: tokens.typography.variant.body2.fontSize,
            lineHeight: tokens.typography.variant.body3.lineHeight,
            color: tokens.colors.text.light.tertirary,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.l,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            padding: 0,
            boxShadow: tokens.shadow.xs,
          },
          colorPrimary: {
            backgroundColor: tokens.colors.bg.light.tertirary,
            color: 'inherit',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          // button: {
          //   '&:hover': {
          //     backgroundColor: 'transparent',
          //   },
          // },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            width: '100%',
            display: 'flex',
            position: 'relative',
            boxSizing: 'border-box',
            textAlign: 'left',
            alignItems: 'center',
            paddingTop: tokens.spacing['8'],
            paddingBottom: tokens.spacing['8'],
            justifyContent: 'flex-start',
            textDecoration: 'none',
            paddingLeft: tokens.spacing['16'],
            paddingRight: tokens.spacing['16'],
            transition:
              'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderCollapse: 'separate',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            background: tokens.colors.bg.light.primary,
            borderBottom: `${tokens.border.s}px solid ${tokens.colors.stroke.primary}`,
            '&:first-of-type': {
              borderLeft: `${tokens.border.s}px solid ${tokens.colors.stroke.primary}`,
            },
            '&:last-child': {
              borderRight: `${tokens.border.s}px solid ${tokens.colors.stroke.primary}`,
            },
          },
          head: {
            color: tokens.colors.text.light.tertirary,
            borderTop: `${tokens.border.s}px solid ${tokens.colors.stroke.primary}`,
            '&:first-of-type': {
              borderTopLeftRadius: tokens.radius.m,
            },
            '&:last-child': {
              borderTopRightRadius: tokens.radius.m,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: tokens.colors.bg.light.primary,
            fontSize: tokens.typography.variant.body3.fontSize,
            color: 'inherit',
            fontWeight: tokens.typography.variant.body3.fontWeight,
            boxShadow: tokens.shadow.s,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          colorDefault: {
            backgroundColor: tokens.colors.original.brand.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          elevation0: {
            border: `${tokens.border.s}px solid ${tokens.colors.stroke.primary}`,
          },
          elevation1: {
            border: `${tokens.border.s}px solid ${tokens.colors.stroke.primary}`,
            boxShadow: 'none',
          },
          elevation2: {
            boxShadow: tokens.shadow.l,
          },
          rounded: {
            borderRadius: tokens.radius.l,
            overflow: 'hidden',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
          icon: {
            color: tokens.colors.text.light.secondary,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          labelPlacementStart: {
            margin: 0,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: px(48),
            height: px(24),
            padding: 0,
            borderRadius: tokens.radius.m,
            marginLeft: px(10), // TODO - fix нет в токенах
          },
          colorPrimary: {
            '&.Mui-checked': {
              color: tokens.colors.text.dark.primary,
              transform: `translateX(${px(24)}px)`,
            },
          },
          switchBase: {
            padding: tokens.spacing['4'],
            '&.Mui-checked': {
              transform: `translateX(${px(24)}px)`,
              '&+.MuiSwitch-track': {
                opacity: 1,
                backgroundColor: tokens.colors.original.brand.primary,
              },
            },
          },
          thumb: {
            width: px(16),
            height: px(16),
            borderRadius: tokens.radius.m,
          },
          track: {
            opacity: 1,
            backgroundColor: tokens.colors.text.light.tertirary,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: tokens.colors.stroke.primary,
            '&.Mui-disabled': {
              color: tokens.colors.bg.greyscale.primary,
            },
          },
          indeterminate: {
            color: tokens.colors.original.brand.primary,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.m,
          },
        },
      },
      ...{
        MuiDateCalendar: {
          styleOverrides: {
            root: {
              overflow: 'visible',
              width: 'fit-content',
              height: 'fit-content',

              '& .MuiDayCalendar-weekDayLabel ': {
                textTransform: 'uppercase',
                color: tokens.colors.text.light.tertirary,
                width: px(32),
                height: px(20),
                '&:nth-child(6), &:nth-child(7)': {
                  color: tokens.colors.accent.text.error,
                },
              },

              '& .MuiDayCalendar-weekContainer': {
                padding: `0 ${tokens.spacing['16']}px`,
                margin: 0,
              },

              '& .MuiPickersSlideTransition-root': {
                overflow: 'visible',
                minHeight: px(178),
                width: px(284),
                marginTop: px(5), // TODO fix - нет в токенах
              },
            },
          },
        },
        MuiPickersDay: {
          styleOverrides: {
            root: {
              margin: 0,

              '&:hover': {
                backgroundColor: tokens.colors.bg.greyscale.primary,
              },
            },
          },
        },
      },
    },
    tokens,
    px,
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'xl'],
      up: (key) =>
        `${typeCssQuery} (min-width:${
          typeof key === 'number' ? key : tokens.breakpoints[key]
        }px)`,
      down: (key) =>
        `${typeCssQuery} (max-width:${
          typeof key === 'number' ? key : tokens.breakpoints[key]
        }px)`,
    },
    typography: {
      fontFamily: tokens.typography.fontFace,
    },
    spacing: 10,
    palette: createPalette(tokens),
  });
};
